import { enqueueForwardTasks } from "@/apis/forward/route";
import { ForwardStatusResponse } from "@/apis/forward/types";
import { useWorkbookId } from "@/hooks/use-workbook";
import { checkForwardFinished } from "@/utils/forward/task-status";
import queries from "@/queries";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";

const FORWARD_POLL_INTERVAL_MS = 2000;

// 판정은 @/utils/forward/task-status 가 소유한다(순수 함수라 단독 테스트 가능).
// 기존 import 경로를 쓰는 호출부를 위해 여기서 다시 노출한다.
export { isForwardRunning } from "@/utils/forward/task-status";

/**
 * 표 단위 forward 진행 상태. auto-tag-status 와 동일하게 단일 폴링 옵저버 원칙을 따른다
 * (enablePolling 은 페이지 루트의 useForwardCompletionEffect 에서만 켠다).
 */
export const useForwardStatus = (
  workbookId: string | undefined,
  options?: { enablePolling?: boolean },
) => {
  const enablePolling = options?.enablePolling ?? false;
  return useQuery({
    ...queries.workbook(workbookId ?? "")._ctx.forwardStatus(),
    enabled: !!workbookId,
    refetchInterval: (query) => {
      if (!enablePolling) return false;
      const data = query.state.data as ForwardStatusResponse | undefined;
      if (data && checkForwardFinished(data)) return false;
      return FORWARD_POLL_INTERVAL_MS;
    },
    gcTime: 0,
    staleTime: 0,
  });
};

/** 확정된 이월 짝으로 워크북 전체 표를 표 단위 실행 enqueue. */
export const useEnqueueForwardTasks = () => {
  const { data: workbookId } = useWorkbookId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["forward-enqueue", workbookId],
    mutationFn: async (variables?: {
      tableIds?: string[];
      smart?: boolean;
    }) => {
      if (!workbookId) throw new Error("Workbook ID is not found");
      return enqueueForwardTasks(
        workbookId,
        variables?.tableIds,
        variables?.smart,
      );
    },
    onSuccess: () => {
      if (!workbookId) return;
      // 롤포워드를 시작한 시점에 그 표의 forwarded 신호가 켜져 다음 액션 추천이 바뀐다.
      // 완료 감지는 진행 모달이 떠 있을 때만 도는데, 추천 쿼리는 staleTime:Infinity라
      // 모달을 닫고 나가면 롤포워드를 이미 했는데도 추천이 그대로 남는다. 시작 시점에
      // 무효화해 모달을 지켜보지 않아도 갱신되게 한다.
      queryClient.invalidateQueries({
        queryKey: queries.workbook(workbookId)._ctx.taggingGuidance().queryKey,
      });
      // Promise 반환 → status refetch(PENDING 도착)까지 isPending 유지.
      return queryClient.invalidateQueries(
        queries.workbook(workbookId)._ctx.forwardStatus(),
      );
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "불러오기 시작 실패",
      );
    },
  });
};
